import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * One Zocdoc schedulable entity — a provider at a location — as the Providers screen shows it.
 *
 * **Not a stored row any more.** It is Zocdoc's own roster joined in memory to the beHuman
 * practitioner carrying that `pr_…` as a FHIR identifier, so the list is current by construction
 * and there is nothing to discover, refresh or let go stale.
 *
 * The configuration columns the old `zocdoc_provider_link` row carried are gone rather than
 * rehomed: `enabled`, `publish_availability`, duration, buffer, horizon and modality were each
 * read by nothing that gates behaviour, so the controls that edited them changed no outcome.
 */
const providerEntitySchema = z.object({
  /** Zocdoc's composite `pr_…|lo_…`, which is this row's identity. No surrogate key any more. */
  zocdocEntityId: z.string(),
  zocdocProviderId: z.string(),
  zocdocLocationId: z.string(),
  /** Zocdoc's own facts about the entity, live from the roster. */
  npi: z.string().optional(),
  timeZone: z.string().optional(),
  mainSpecialtyName: z.string().optional(),
  goLiveAt: z.string().optional(),
  /** The beHuman practitioner carrying this provider id. Absent means nobody claims it. */
  practitionerId: z.string().optional(),
  practitionerName: z.string().optional(),
  /** From the practitioner's `PractitionerRole`, not a stored column. */
  organizationId: z.string().optional(),
})

export const zocdocProviderLinks = oc
  .route({ method: 'GET', path: '/v2/zocdoc/provider-links' })
  .output(
    z.object({
      links: z.array(providerEntitySchema),
      summary: z.object({
        /** Entities on Zocdoc's roster right now. */
        total: z.number(),
        /** Of those, how many a beHuman practitioner claims. */
        mapped: z.number(),
      }),
      /**
       * The roster read failed, so `links` is empty rather than wrong. The practitioner join
       * failing is reported separately — see `practitionerError`.
       */
      error: z.string().optional(),
      /** The roster arrived but the practitioner join did not: every row shows as unmapped. */
      practitionerError: z.string().optional(),
      missingEnv: z.array(z.string()).optional(),
    }),
  )
